import React from "react";

import { css, cx } from "@emotion/css";
import MuiSlider from "@mui/material/Slider";

import { useStyles } from "@/hooks/useStyles.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

export type SliderMark = {
  value: number;
  label?: React.ReactNode;
};

export type BaseSliderProps<T> = {
  min: number;
  max: number;
  step?: number | null;
  value: T;
  scale?: (value: number) => number;
  marks?: SliderMark[];
  track?: "normal" | false | "inverted";
  onChange: (value: T, activeThumb: number) => void;
  valueLabelDisplay?: "auto" | "on" | "off";
};

export type SliderProps<T> = AtomProps<BaseSliderProps<T>, StyleClasses<"root">>;

export const Slider = <T extends number | number[] = number>({
  id,
  value,
  onChange,
  className,
  classes,
  ...props
}: SliderProps<T>): React.ReactElement => {
  const styles = useStyles(makeStyles);

  return (
    <MuiSlider
      id={id}
      value={value as T}
      className={cx(styles.root, className)}
      onChange={(_, value, activeThumb) => onChange(value as T, activeThumb)}
      {...props}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    "& .MuiSlider-track": {
      height: theme.spacing(2),
      border: "none",
    },
    "& .MuiSlider-trail": {
      width: theme.spacing(2),
      border: "none",
    },
  }),
});
